import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Auth} from '../actions/auth.actions';
import Login = Auth.Login;
import {tap} from 'rxjs/operators';
import Logout = Auth.Logout;
import GetOrganizationModules = Auth.GetOrganizationModules;
import {OrganizationService} from '../services/organization.service';
import {Observable} from 'rxjs';
import {AuthService} from '../services/auth.service';
import {LoginResponse} from '../models/auth/login.response';
import ClearState = Auth.ClearState;

export interface AuthStateModel {
  token: string | null;
  expires: Date | null;
  username: string | null;
  userId: string | null;
  roles: string[];
  organization: string | null;
  organizationModules: string[];
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    token: null,
    expires: null,
    username: null,
    userId: null,
    roles: [],
    organization: null,
    organizationModules: [],
  }
})
@Injectable()
export class AuthState {
  constructor(private authService: AuthService, private organizationService: OrganizationService) {
  }

  @Selector()
  static token(state: AuthStateModel): string | null {
    return state.token;
  }

  @Selector()
  static organization(state: AuthStateModel): string | null {
    return state.organization;
  }

  @Selector()
  static expires(state: AuthStateModel): Date | null {
    return state.expires;
  }

  @Selector()
  static modules(state: AuthStateModel): string[] {
    return state.organizationModules;
  }

  @Selector()
  static roles(state: AuthStateModel): string[] {
    return state.roles;
  }

  @Selector()
  static userId(state: AuthStateModel): string {
    return state.userId;
  }

  @Selector()
  static username(state: AuthStateModel): string {
    return state.username;
  }

  @Selector()
  static isAuthenticated(state: AuthStateModel): boolean {
    const now = new Date();
    if (now < new Date(state.expires)) {
      return !!state.token;
    }
    return false;
  }

  @Action(Login)
  login(ctx: StateContext<AuthStateModel>, action: Login): Observable<LoginResponse> {
    return this.authService.login(action.payload).pipe(
      tap((result: LoginResponse) => {
        ctx.patchState({
          token: result.jwt,
          username: action.payload.username,
          expires: result.expires,
          userId: result.userId,
          roles: result.roles,
          organization: action.payload.organizationHandle
        });
      })
    );
  }

  @Action(GetOrganizationModules)
  getOrganizationModules(ctx: StateContext<AuthStateModel>): Observable<string[]> {
    return this.organizationService.getOrganizationModules().pipe(
      tap((result: string[]) => {
        ctx.patchState({
          organizationModules: result
        });
      })
    );
  }

  @Action(Logout)
  logout(ctx: StateContext<AuthStateModel>): void {
    const state = ctx.getState();
    ctx.setState({
      token: null,
      username: null,
      userId: null,
      roles: [],
      expires: null,
      organization: state.organization,
      organizationModules: []
    });
  }

  @Action(ClearState)
  clearState(ctx: StateContext<AuthStateModel>): void {
    const state = ctx.getState();
    ctx.setState({
      token: null,
      username: null,
      userId: null,
      roles: [],
      expires: null,
      organization: state.organization,
      organizationModules: []
    });
  }
}
